/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace component-selector */

import {
  Injectable,
  OnDestroy,
  ComponentFactoryResolver,
  ViewContainerRef
}                                  from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  Subject
}                                  from 'rxjs';

import {
  WindowType
}                                  from '@/screens/ide/window';

import { Logger }                  from '@/util/logger';
import { AuthenticationService }   from "@/services/authentication.service";
import { ProjectService }          from "@/services/project.service";
import { WindowService }           from "@/services/window.service";

import { DocumentWindowManager }   from '@/screens/ide/document.window.manager';
import { DocumentMetadata      }   from '@/screens/ide/document.metadata';
import { PropertiesHolder }        from '@/models/properties.holder';
import { SimulationGroupElement }  from '@/models/project/simulation.group.element';
import { BlobElement }             from '@/models/project/blob.element';
import {ViewMemento} from "@/screens/ide/stringviewer/view.memento";

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class IDEService
 *
 * @brief from anywhere in our application we can do operations on the main IDE, for example opening or
 * closing particular window.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class IdeService implements OnDestroy {

  /* the default zoom/position for string line viewers */

  private defaultViewSubject: BehaviorSubject<ViewMemento>;
  private defaultView:        Observable<ViewMemento>;

  /* the currently active selection/focus */

  private currentFocusSubject: BehaviorSubject<PropertiesHolder>;
  private currentFocus:        Observable<PropertiesHolder>;

  /* the currently focused simulation */

  private simulationSubject:       BehaviorSubject<SimulationGroupElement>;
  private simulationObservable:    Observable<SimulationGroupElement>;

  /* the currently focused blob */

  private blobSubject:       BehaviorSubject<BlobElement>;
  private blobObservable:    Observable<BlobElement>;

  /** track what the view container of /ide is so we can dynamically add components on the IDE screen */
  private ideView: ViewContainerRef = null;

  /** clean up when service is destroyed */
  ngOnDestroy(): void {

    Logger.debug('[IDEService] destructing...');

  }

  /** standard constructor */
  constructor(
    private projects:    ProjectService,
    private auth:        AuthenticationService,
    private windows:     WindowService,
    private cfr:         ComponentFactoryResolver
  ) {

    this.defaultViewSubject   = new BehaviorSubject<ViewMemento>(null);
    this.defaultView          = this.defaultViewSubject.asObservable();

    this.currentFocusSubject   = new BehaviorSubject<PropertiesHolder>(null);
    this.currentFocus          = this.currentFocusSubject.asObservable();

    this.simulationSubject     = new BehaviorSubject<SimulationGroupElement>(null);
    this.simulationObservable  = this.simulationSubject.asObservable();

    this.blobSubject           = new BehaviorSubject<BlobElement>(null);
    this.blobObservable        = this.blobSubject.asObservable();

    Logger.debug('[IDEService] constructing...');

    let ide: IdeService = this;

    ide.auth.observableUser.subscribe(user => {

      /* set the project panel etc. based on the logged-in user... */

      if (user) {

        if (ide.projects.user()) {

          if (user.username === ide.projects.user().username) {

            /* they just refreshed their login session */

            Logger.debug('[IDEService] user refreshed.');

            return;
          }
        }

        /* still an active user! */

        Logger.debug('[IDEService] user switched to: ', user);

        ide.projects.setUser(user);

        /* try to use the last active project (if there is one) */

        ide.projects.lastActive();

        return;
      }

      Logger.debug('[IDEService] logging out...');

      /*
       * leave the last thing they were viewing in their recent history.
       *
       ide.projects.setProject(null);
       *
       */

    });

  }

  /** give the service a reference to the central document tabs */
  setDocumentManager(documents: DocumentWindowManager) {

    return this.windows.setDocumentManager(documents);
  }

  /** launch a tab, but try to use the anchored window for this kind window */
  refocus(params: DocumentMetadata, append: boolean = false) {

    return this.windows.refocus(params, append);
  }

  /** add a new tab to the central part of the GUI */
  launchWindow(params: DocumentMetadata) {

    return this.windows.launchWindow(params);
  }

  /** subscribe to window toggle requests */
  onWindowToggle(): Observable<WindowType> {

    return this.windows.onWindowToggle();
  }

  /** toggle a window open/close */
  toggleWindow(type: WindowType) {

    /* notify the project manager that it needs to toggle this window */

    return this.windows.toggleWindow(type);
  }






  /** once available, the IDE component must tell us what its view container is */
  public setRootViewContainerRef(view: ViewContainerRef) {
    this.ideView = view;
  }

  /** fetch the view container for /ide (allows dynamically adding components */
  public  get view(): ViewContainerRef {
    return this.ideView;
  }

  /** fetch the component factory resolver, we need this to dynamically create components */
  public get componentResolver(): ComponentFactoryResolver {
    return this.cfr;
  }

  /** set the simulation which is now in focus */
  setCurrentSimulation(simulation: SimulationGroupElement) {

    this.simulationSubject.next(simulation);
  }

  /** set the bloblwhich is now in focus (i.e. to be simulated) */
  setCurrentBlob(blob: BlobElement) {

    this.blobSubject.next(blob);
  }

  /** subscribe to the current simulation in focus */
  public get activeSimulation(): Observable<SimulationGroupElement> {

    return this.simulationObservable;
  }

  /** subscribe to the current blob in focus */
  public get activeBlob(): Observable<BlobElement> {

    return this.blobObservable;
  }

  /** fetch the observable for the default string line viewer zoom/position */
  public get activeStringLineView(): Observable<ViewMemento> {

    return this.defaultView;
  }

  /** fetch the default string line viewer zoom/position (if there is one) */
  public get defaultStringLineView(): ViewMemento {

    return this.defaultViewSubject.value;
  }

  /** set the new default zoom / position for string line viewers */
  stringLineView(view: ViewMemento): void {

    this.defaultViewSubject.next(view);
  }

  /** fetch the user's active selection/focus */
  public get activeFocus(): Observable<PropertiesHolder> {

    return this.currentFocus;
  }

  /** fetch the current selection/focus */
  public get focusOfUser(): PropertiesHolder {

    return this.currentFocusSubject.value;
  }

  /** tell other parts of the IDE that the focus/selection has changed */
  focus(properties: PropertiesHolder): void {

    this.currentFocusSubject.next(properties);
  }

  destroy(): void {

  }

}
